/**
 * Calculator Functions for Krinos HVAC Assessment Platform
 *
 * This file contains all core calculation logic for HVAC system analysis.
 * All functions are pure (no side effects) for testability.
 *
 * @module calculatorFunctions
 */

import {
  AFUE_BY_AGE,
  AGE_THRESHOLDS,
  CLIMATE_ZONE_4A,
  DUCT_EFFICIENCY,
  EFFICIENCY_DEGRADATION,
  ENERGY_COST_PARAMS,
  ENVELOPE_FACTORS,
  GAS_PRICE_NC,
  HSPF2_CONVERSION,
  MAINTENANCE_PARAMS,
  NEW_SYSTEM_TIERS,
  RECOMMENDATION_THRESHOLDS,
  SCORING_PARAMS,
  SEER2_CONVERSION,
  SEER_BY_INSTALLATION_YEAR,
  VALIDATION_LIMITS
} from './calculatorConstants.js';

/* =========================
   Conversion Utilities
   ========================= */

/**
 * Convert SEER to SEER2 based on system type
 * @param {number} seer - SEER rating
 * @param {string} systemType - 'split' or 'packaged' (default: 'split')
 * @returns {number} SEER2 rating
 */
export function convertToSEER2(seer, systemType = 'split') {
  const conversionFactor = systemType === 'packaged'
    ? SEER2_CONVERSION.packaged
    : SEER2_CONVERSION.split;
  return seer * conversionFactor;
}

/**
 * Convert HSPF to HSPF2
 * @param {number} hspf - HSPF rating
 * @returns {number} HSPF2 rating
 */
export function convertToHSPF2(hspf) {
  return hspf * HSPF2_CONVERSION;
}

/**
 * Convert display SEER back from SEER2
 * @param {number} seer2 - SEER2 rating
 * @param {string} systemType - 'split' or 'packaged' (default: 'split')
 * @returns {number} SEER rating
 */
export function convertFromSEER2(seer2, systemType = 'split') {
  const conversionFactor = systemType === 'packaged'
    ? SEER2_CONVERSION.packaged
    : SEER2_CONVERSION.split;
  return seer2 / conversionFactor;
}

/* =========================
   SEER & Efficiency Calculations
   ========================= */

/**
 * Estimate original SEER based on system age
 * @param {number} age - System age in years
 * @returns {number} Estimated original SEER rating
 */
export function estimateOriginalSEER(age) {
  const currentYear = new Date().getFullYear();
  const installYear = currentYear - age;

  // Find the appropriate year range
  if (installYear >= 2023) return SEER_BY_INSTALLATION_YEAR[2023].typical;
  if (installYear >= 2015) return SEER_BY_INSTALLATION_YEAR[2015].typical;
  if (installYear >= 2006) return SEER_BY_INSTALLATION_YEAR[2006].typical;
  if (installYear >= 1998) return SEER_BY_INSTALLATION_YEAR[1998].typical;
  if (installYear >= 1992) return SEER_BY_INSTALLATION_YEAR[1992].typical;
  if (installYear >= 1980) return SEER_BY_INSTALLATION_YEAR[1980].typical;
  return SEER_BY_INSTALLATION_YEAR[1970].typical;
}

/**
 * Apply degradation to efficiency based on age
 * @param {number} originalEfficiency - Original efficiency rating
 * @param {number} age - System age in years
 * @returns {number} Current efficiency with degradation applied
 */
export function applyEfficiencyDegradation(originalEfficiency, age) {
  const degradation = Math.min(
    age * EFFICIENCY_DEGRADATION.annualRate,
    EFFICIENCY_DEGRADATION.maxDegradation
  );
  return originalEfficiency * (1 - degradation);
}

/**
 * Calculate composite envelope score (Phase 3)
 * @param {Object} envelope - Envelope data
 * @param {string} envelope.airTightness - Air tightness level
 * @param {string} envelope.insulation - Insulation quality
 * @param {string} envelope.windows - Window type
 * @returns {number} Composite envelope multiplier
 */
export function calculateEnvelopeScore(envelope) {
  const airFactor = ENVELOPE_FACTORS.airTightness[envelope.airTightness]?.multiplier || 1.10;
  const insulationFactor = ENVELOPE_FACTORS.insulation[envelope.insulation]?.multiplier || 1.00;
  const windowFactor = ENVELOPE_FACTORS.windows[envelope.windows]?.multiplier || 1.00;

  // Average the three factors
  return (airFactor + insulationFactor + windowFactor) / 3;
}

/**
 * Calculate current SEER with improved estimation and degradation
 * @param {Object} data - System data
 * @returns {number} Current SEER rating (rounded to 1 decimal)
 */
export function calculateCurrentSeer(data) {
  // If user provided original SEER, use it; otherwise estimate
  const originalSEER = data.originalSeer || estimateOriginalSEER(data.age);

  // Apply degradation
  const currentSEER = applyEfficiencyDegradation(originalSEER, data.age);

  // Ensure minimum of 6 SEER
  return Math.max(6, Math.round(currentSEER * 10) / 10);
}

/* =========================
   Gas Furnace Calculations (Phase 5)
   ========================= */

/**
 * Estimate AFUE for gas furnace based on age
 * @param {number} age - System age in years
 * @param {boolean} isHighEfficiency - Whether it's a high-efficiency unit
 * @returns {number} Estimated AFUE percentage
 */
export function estimateAFUE(age, isHighEfficiency = false) {
  // Find the closest age bracket
  const ages = Object.keys(AFUE_BY_AGE).map(Number).sort((a, b) => a - b);
  let closestAge = ages[0];

  for (let i = 0; i < ages.length; i++) {
    if (age >= ages[i]) {
      closestAge = ages[i];
    } else {
      break;
    }
  }

  const afueData = AFUE_BY_AGE[closestAge];
  return isHighEfficiency ? afueData.highEfficiency : afueData.standard;
}

/**
 * Calculate annual gas heating cost
 * @param {Object} data - System data (must include squareFootage, age, and optionally current_afue)
 * @param {Object} climate - Climate data with heating hours
 * @param {number} gasRate - Optional custom gas rate ($/therm), defaults to NC average
 * @returns {number} Annual gas heating cost in dollars
 */
export function calculateGasHeatingCost(data, climate = CLIMATE_ZONE_4A, gasRate = null) {
  const heatingLoadBtu = data.squareFootage * 30; // BTU/hr per sq ft for heating
  // Use provided current_afue if available, otherwise estimate from age
  const currentAFUE = data.current_afue || estimateAFUE(data.age);
  // AFUE is stored as percentage (e.g., 78), convert to decimal by dividing by 100
  // Formula: total BTU needed / efficiency decimal / BTU per therm
  const annualTherms = (heatingLoadBtu * climate.heatingHours) / ((currentAFUE / 100) * 100000);
  const rate = gasRate !== null ? gasRate : GAS_PRICE_NC;
  return Math.round(annualTherms * rate);
}

/* =========================
   Energy Cost Estimation
   ========================= */

/**
 * Estimate annual energy cost based on system characteristics
 * @param {Object} params - Calculation parameters
 * @returns {number} Estimated annual energy cost
 */
export function estimateEnergyCost(params) {
  const {
    systemType = 'centralAC',
    age = 8,
    originalSeer = null,
    squareFootage = 2000,
    ductCondition = 'good',
    issues = {},
    envelope = null,
    climateData = null,
    electricRate = null,
    gasRate = null
  } = params;

  // Use provided climate data or fall back to default (Zone 4A)
  const climate = climateData || CLIMATE_ZONE_4A;

  // Use provided rates or fall back to defaults
  const useElectricRate = electricRate !== null ? electricRate : ENERGY_COST_PARAMS.electricRate;
  const useGasRate = gasRate !== null ? gasRate : GAS_PRICE_NC;

  // Calculate current SEER (with degradation)
  const currentSEER = originalSeer || estimateOriginalSEER(age);
  const degradedSEER = applyEfficiencyDegradation(currentSEER, age);
  const currentSEER2 = degradedSEER * SEER2_CONVERSION.split;

  // Calculate cooling load (BTU per hour)
  let coolingLoadBtu = squareFootage * 20; // Base BTU/hr per sq ft

  // Apply envelope adjustments if provided
  if (envelope) {
    const envelopeScore = calculateEnvelopeScore(envelope);
    coolingLoadBtu *= envelopeScore;
  }

  // Apply duct efficiency
  const ductEfficiency = DUCT_EFFICIENCY[ductCondition] || 0.82;

  // Calculate annual cooling cost (electric)
  let annualCoolingCost = 0;
  if (systemType !== 'furnaceOnly' && systemType !== 'boiler') {
    // System has cooling capability
    const coolingHours = climate.coolingHours;
    const annualCoolingKwh = (coolingLoadBtu * coolingHours) / (currentSEER2 * 1000) / ductEfficiency;
    annualCoolingCost = annualCoolingKwh * useElectricRate;
  }

  // Calculate annual heating cost based on system type
  let annualHeatingCost = 0;
  const heatingLoadBtu = squareFootage * 30; // BTU/hr per sq ft for heating
  const heatingHours = climate.heatingHours;

  if (systemType === 'furnaceAC' || systemType === 'furnaceACPackage' || systemType === 'furnaceOnly' || systemType === 'boiler') {
    // Gas heating systems
    const currentAFUE = estimateAFUE(age);
    // AFUE is percentage (e.g., 78), convert to decimal by dividing by 100
    const annualTherms = (heatingLoadBtu * heatingHours) / ((currentAFUE / 100) * 100000);
    annualHeatingCost = annualTherms * useGasRate;
  } else if (systemType === 'heatPump' || systemType === 'heatPumpPackage') {
    // Heat pump - electric heating
    // HSPF approximation: use SEER / 1.2 as rough estimate for older systems
    const estimatedHSPF = degradedSEER / 1.2;
    const estimatedHSPF2 = estimatedHSPF * HSPF2_CONVERSION;
    const annualHeatingKwh = (heatingLoadBtu * heatingHours) / (estimatedHSPF2 * 1000) / ductEfficiency;
    annualHeatingCost = annualHeatingKwh * useElectricRate;
  } else if (systemType === 'ductlessMini') {
    // Ductless mini-split - assume it provides heating (no duct losses)
    const estimatedHSPF = degradedSEER / 1.1; // Slightly more efficient than ducted
    const estimatedHSPF2 = estimatedHSPF * HSPF2_CONVERSION;
    const annualHeatingKwh = (heatingLoadBtu * heatingHours) / (estimatedHSPF2 * 1000); // No duct efficiency factor
    annualHeatingCost = annualHeatingKwh * useElectricRate;
  }
  // Note: centralAC has no heating

  // Combine costs
  let totalAnnual = annualCoolingCost + annualHeatingCost;

  // Comfort issues are used for recommendation scoring, not energy cost calculation
  // Energy cost should be based purely on measurable physics: SEER, age, climate, rates, ducts

  // Round to nearest $50
  totalAnnual = Math.round(totalAnnual / 50) * 50;

  // Apply minimum
  totalAnnual = Math.max(totalAnnual, ENERGY_COST_PARAMS.minAnnualCost);

  return totalAnnual;
}

/* =========================
   Efficiency & Savings Analysis
   ========================= */

/**
 * Calculate efficiency savings comparing current system to new options
 * @param {number} currentSeer - Current SEER rating
 * @param {number} annualCost - Current annual energy cost
 * @param {Object} data - System data
 * @param {Object} climate - Climate data with cooling/heating hours
 * @param {Object} customRates - Optional custom utility rates {electricRate, gasRate}
 * @returns {Object} Efficiency analysis with savings projections
 */
export function calculateEfficiencySavings(currentSeer, annualCost, data, climate = CLIMATE_ZONE_4A, customRates = null) {
  // Convert current SEER to SEER2 for calculations
  const currentSeer2 = currentSeer * SEER2_CONVERSION.split;

  // Calculate base cooling load (BTU per hour)
  const coolingLoadBtu = data.squareFootage * 20; // Base BTU/hr per sq ft

  // Apply envelope adjustments to load
  const envelope = data.envelope || { airTightness: 'average', insulation: 'average', windows: 'double' };
  const envelopeScore = calculateEnvelopeScore(envelope);
  const adjustedCoolingLoad = coolingLoadBtu * envelopeScore;

  // Duct efficiency impact
  const currentDuctEfficiency = DUCT_EFFICIENCY[data.ductCondition] || 0.82;

  // Calculate energy costs using proper formulas
  const coolingHours = climate.coolingHours;
  const electricRate = customRates?.electricRate !== undefined ? customRates.electricRate : ENERGY_COST_PARAMS.electricRate;
  const gasRate = customRates?.gasRate !== undefined ? customRates.gasRate : null;

  // Current system annual cooling kWh (with duct losses)
  const currentAnnualKwh = (adjustedCoolingLoad * coolingHours) / (currentSeer2 * 1000) / currentDuctEfficiency;
  const currentAnnualCoolingCost = currentAnnualKwh * electricRate;

  // Handle different system types
  let baseAnnualCost;
  if (data.systemType === 'furnaceOnly') {
    // Gas furnace only - calculate heating costs
    baseAnnualCost = calculateGasHeatingCost(data, climate, gasRate);
  } else if (data.systemType === 'furnaceAC') {
    // Dual fuel - cooling (electric) + heating (gas)
    const gasHeatingCost = calculateGasHeatingCost(data, climate, gasRate);
    baseAnnualCost = currentAnnualCoolingCost + gasHeatingCost;
  } else if (data.systemType === 'heatPump' || data.systemType === 'heatPumpPackage') {
    // Heat pump - calculate BOTH cooling AND heating
    const heatingLoadBtu = data.squareFootage * 30;
    const currentHSPF = currentSeer / 1.2;
    const currentHSPF2 = currentHSPF * HSPF2_CONVERSION;
    const currentHeatingKwh = (heatingLoadBtu * climate.heatingHours) / (currentHSPF2 * 1000) / currentDuctEfficiency;
    const currentHeatingCost = currentHeatingKwh * electricRate;
    baseAnnualCost = currentAnnualCoolingCost + currentHeatingCost;
  } else if (data.systemType === 'ductlessMini') {
    // Ductless mini-split (typically both heating and cooling)
    const heatingLoadBtu = data.squareFootage * 30;
    const currentHSPF = currentSeer / 1.2;
    const currentHSPF2 = currentHSPF * HSPF2_CONVERSION;
    const currentHeatingKwh = (heatingLoadBtu * climate.heatingHours) / (currentHSPF2 * 1000);  // No duct losses
    const currentHeatingCost = currentHeatingKwh * electricRate;
    baseAnnualCost = currentAnnualCoolingCost + currentHeatingCost;
  } else {
    // AC-only systems (centralAC)
    baseAnnualCost = currentAnnualCoolingCost;
  }

  // Validation: Apply sanity checks
  if (data.systemType !== 'furnaceOnly') {
    // Heat pumps and ductless can have much higher costs (heating + cooling)
    const maxLimit = (data.systemType === 'heatPump' || data.systemType === 'heatPumpPackage' || data.systemType === 'ductlessMini')
      ? 15000  // Heat pumps/ductless with both heating and cooling
      : VALIDATION_LIMITS.coolingCost.max;  // AC-only systems

    baseAnnualCost = Math.max(
      VALIDATION_LIMITS.coolingCost.min,
      Math.min(maxLimit, baseAnnualCost)
    );
  } else {
    baseAnnualCost = Math.max(
      VALIDATION_LIMITS.heatingCost.min,
      Math.min(VALIDATION_LIMITS.heatingCost.max, baseAnnualCost)
    );
  }

  // Calculate savings for each efficiency tier
  const options = Object.entries(NEW_SYSTEM_TIERS).map(([tier, config]) => {
    const seer2 = data.systemType === 'ductlessMini'
      ? config.seer2.ductlessMini
      : config.seer2.default;

    let newAnnualCost;

    if (data.systemType === 'furnaceOnly') {
      // Gas furnace replacement savings
      const newAFUE = config.afue;
      const heatingLoadBtu = data.squareFootage * 30;
      // AFUE is percentage, convert to decimal
      const newAnnualTherms = (heatingLoadBtu * climate.heatingHours) / ((newAFUE / 100) * 100000);
      const useGasRate = gasRate !== null ? gasRate : GAS_PRICE_NC;
      newAnnualCost = Math.round(newAnnualTherms * useGasRate);
    } else {
      // Electric cooling systems
      const newDuctEfficiency = 0.95;
      const newAnnualKwh = (adjustedCoolingLoad * coolingHours) / (seer2 * 1000) / newDuctEfficiency;
      const newCoolingCost = newAnnualKwh * electricRate;

      if (data.systemType === 'furnaceAC') {
        // Dual fuel - add new gas heating cost
        const newAFUE = config.afue;
        const heatingLoadBtu = data.squareFootage * 30;
        // AFUE is percentage, convert to decimal
        const newAnnualTherms = (heatingLoadBtu * climate.heatingHours) / ((newAFUE / 100) * 100000);
        const useGasRate = gasRate !== null ? gasRate : GAS_PRICE_NC;
        const newGasCost = Math.round(newAnnualTherms * useGasRate);
        newAnnualCost = newCoolingCost + newGasCost;
      } else if (data.systemType === 'heatPump' || data.systemType === 'heatPumpPackage') {
        // Heat pump - add new heating cost
        const heatingLoadBtu = data.squareFootage * 30;
        const newHSPF2 = seer2 * 0.85;  // Modern heat pumps: HSPF2 ≈ SEER2 × 0.85
        const newHeatingKwh = (heatingLoadBtu * climate.heatingHours) / (newHSPF2 * 1000) / newDuctEfficiency;
        const newHeatingCost = newHeatingKwh * electricRate;
        newAnnualCost = newCoolingCost + newHeatingCost;
      } else if (data.systemType === 'ductlessMini') {
        // Ductless mini-split - add heating cost (no duct losses)
        const heatingLoadBtu = data.squareFootage * 30;
        const newHSPF2 = seer2 * 0.90;  // Ductless is more efficient
        const newHeatingKwh = (heatingLoadBtu * climate.heatingHours) / (newHSPF2 * 1000);  // No duct losses
        const newHeatingCost = newHeatingKwh * electricRate;
        newAnnualCost = newCoolingCost + newHeatingCost;
      } else {
        // AC-only systems (centralAC)
        newAnnualCost = newCoolingCost;
      }
    }

    let annualSavings = Math.round(baseAnnualCost - newAnnualCost);
    if (annualSavings < 0) annualSavings = 0;

    // Apply 3% annual energy cost escalation over 10 years
    const tenYearSavings = Math.round(annualSavings * 10 * 1.15); // 1.15 accounts for 3% escalation

    // Calculate display SEER from SEER2 for customer understanding
    const displaySeer = Math.round(seer2 / SEER2_CONVERSION.split);

    return {
      name: config.name,
      description: config.description,
      seer: displaySeer,  // Traditional SEER for display
      seer2: seer2,
      annualSavings,
      tenYearSavings,
      percentSavings: Math.max(0, Math.round((1 - newAnnualCost / baseAnnualCost) * 100))
    };
  });

  return {
    currentSeer,
    currentSeer2: Math.round(currentSeer2 * 10) / 10,
    newOptions: options,
    ductEfficiency: {
      current: currentDuctEfficiency,
      new: 0.95
    }
  };
}

/* =========================
   Maintenance Projections
   ========================= */

/**
 * Compute maintenance projection for current system
 * @param {Object} data - System data
 * @param {Object} climate - Climate data (not currently used but included for future expansion)
 * @returns {Object} Maintenance projection
 */
export function computeMaintenanceProjection(data, climate = CLIMATE_ZONE_4A) {
  const { age, refrigerant, ductCondition, squareFootage } = data;

  /**
   * Calculate annual maintenance cost for a given system age
   */
  function getAnnualCost(systemAge, refrigType) {
    // Get base cost by age
    let base;
    if (systemAge <= 2) base = MAINTENANCE_PARAMS.annualCostByAge[0];
    else if (systemAge <= 5) base = MAINTENANCE_PARAMS.annualCostByAge[3];
    else if (systemAge <= 8) base = MAINTENANCE_PARAMS.annualCostByAge[6];
    else if (systemAge <= 10) base = MAINTENANCE_PARAMS.annualCostByAge[9];
    else if (systemAge <= 12) base = MAINTENANCE_PARAMS.annualCostByAge[11];
    else if (systemAge <= 15) base = MAINTENANCE_PARAMS.annualCostByAge[13];
    else base = MAINTENANCE_PARAMS.annualCostByAge[16];

    // Refrigerant multiplier
    base *= (MAINTENANCE_PARAMS.refrigerantMultiplier[refrigType] || 1.1);

    // Usage intensity (based on square footage as proxy)
    let usageMultiplier = 1.0;
    if (squareFootage < 1500) usageMultiplier = 0.9;  // light usage
    else if (squareFootage > 3000) usageMultiplier = 1.1; // heavy usage
    base *= usageMultiplier;

    // Duct condition
    base *= (MAINTENANCE_PARAMS.ductConditionMultiplier[ductCondition] || 1.02);

    return Math.round(base);
  }

  // 5-year horizon
  const year1 = getAnnualCost(age + 1, refrigerant);
  const year2 = getAnnualCost(age + 2, refrigerant);
  const year3 = getAnnualCost(age + 3, refrigerant);
  const year4 = getAnnualCost(age + 4, refrigerant);
  const year5 = getAnnualCost(age + 5, refrigerant);
  let fiveYearTotal = year1 + year2 + year3 + year4 + year5;

  // Major repair probability
  let repairProb = 0;
  if (age <= 5) repairProb = MAINTENANCE_PARAMS.majorRepairProbability[0];
  else if (age <= 10) repairProb = MAINTENANCE_PARAMS.majorRepairProbability[6];
  else if (age <= 15) repairProb = MAINTENANCE_PARAMS.majorRepairProbability[11];
  else repairProb = MAINTENANCE_PARAMS.majorRepairProbability[16];

  const expectedMajorRepair = Math.round(repairProb * MAINTENANCE_PARAMS.avgMajorRepair);
  fiveYearTotal += expectedMajorRepair;

  // New system predictable maintenance
  const newSystemAnnual = MAINTENANCE_PARAMS.newSystemAnnual;
  const newSystemFiveYear = newSystemAnnual * 5;

  return {
    currentAnnualNext: year1,
    fiveYearTotal,
    newSystemAnnual,
    newSystemFiveYear,
    majorRepairRisk: repairProb,
    expectedMajorRepair
  };
}

/* =========================
   Scoring & Assessment
   ========================= */

/**
 * Calculate assessment score with breakdown
 * @param {Object} data - System data
 * @returns {Object} Score and breakdown
 */
export function calculateAssessmentScore(data) {
  let assessmentScore = 0;
  const scoreBreakdown = [];

  // Young system penalty
  let youngSystemPenalty = 0;
  if (data.age < SCORING_PARAMS.youngSystemPenalty.veryYoung.maxAge) {
    youngSystemPenalty = SCORING_PARAMS.youngSystemPenalty.veryYoung.penalty;
  } else if (data.age < SCORING_PARAMS.youngSystemPenalty.young.maxAge) {
    youngSystemPenalty = SCORING_PARAMS.youngSystemPenalty.young.penalty;
  }

  // Age scoring
  if (data.age <= SCORING_PARAMS.agePoints.veryYoung.maxAge) {
    assessmentScore += SCORING_PARAMS.agePoints.veryYoung.points;
    scoreBreakdown.push({
      factor: 'System Age',
      value: `${data.age} years (young - should last many more years)`,
      points: SCORING_PARAMS.agePoints.veryYoung.points
    });
  } else if (data.age <= SCORING_PARAMS.agePoints.young.maxAge) {
    assessmentScore += SCORING_PARAMS.agePoints.young.points;
    scoreBreakdown.push({
      factor: 'System Age',
      value: `${data.age} years (middle-aged)`,
      points: SCORING_PARAMS.agePoints.young.points
    });
  } else if (data.age <= SCORING_PARAMS.agePoints.middleAge.maxAge) {
    assessmentScore += SCORING_PARAMS.agePoints.middleAge.points;
    scoreBreakdown.push({
      factor: 'System Age',
      value: `${data.age} years (approaching typical lifespan)`,
      points: SCORING_PARAMS.agePoints.middleAge.points
    });
  } else if (data.age <= SCORING_PARAMS.agePoints.atLifespan.maxAge) {
    assessmentScore += SCORING_PARAMS.agePoints.atLifespan.points;
    scoreBreakdown.push({
      factor: 'System Age',
      value: `${data.age} years (at/past typical lifespan)`,
      points: SCORING_PARAMS.agePoints.atLifespan.points
    });
  } else {
    assessmentScore += SCORING_PARAMS.agePoints.pastLifespan.points;
    scoreBreakdown.push({
      factor: 'System Age',
      value: `${data.age} years (well past typical lifespan)`,
      points: SCORING_PARAMS.agePoints.pastLifespan.points
    });
  }

  // Repair history
  if (data.lastRepair && data.lastRepair !== 'never') {
    const points = SCORING_PARAMS.repairHistoryPoints[data.lastRepair] || 0;
    assessmentScore += points;
    const labels = {
      'thisYear': 'Recent repairs',
      '1-2years': 'Repairs 1–2 years ago',
      '2+years': 'Repairs 2+ years ago'
    };
    scoreBreakdown.push({
      factor: 'Repair History',
      value: labels[data.lastRepair],
      points
    });
  }

  // Refrigerant
  const refrPoints = SCORING_PARAMS.refrigerantPoints[data.refrigerant] || 0;
  if (refrPoints > 0) {
    assessmentScore += refrPoints;
    const labels = {
      r410a: 'R-410A (phasing out)',
      r22: 'R-22 (obsolete, expensive)'
    };
    scoreBreakdown.push({
      factor: 'Refrigerant Type',
      value: labels[data.refrigerant],
      points: refrPoints
    });
  }

  // Comfort issues with age-based scaling
  let comfortCount = 0;
  const issueCats = { efficiency: [], comfort: [], quality: [] };

  if (data.issues.hotCold) { comfortCount++; issueCats.comfort.push('Temperature imbalance'); }
  if (data.issues.humidity) { comfortCount++; issueCats.comfort.push('Humidity control'); }
  if (data.issues.runsConstantly) { comfortCount++; issueCats.efficiency.push('Constant operation'); }
  if (data.issues.highBills) { comfortCount++; issueCats.efficiency.push('High energy costs'); }
  if (data.issues.loud) { comfortCount++; issueCats.quality.push('Noise issues'); }
  if (data.issues.tempTrouble) { comfortCount++; issueCats.comfort.push('Temperature maintenance'); }

  if (comfortCount > 0) {
    // Age-based multiplier for comfort issues
    let comfortMultiplier = 1.0;
    if (data.age <= SCORING_PARAMS.comfortMultiplierByAge.young.maxAge) {
      comfortMultiplier = SCORING_PARAMS.comfortMultiplierByAge.young.multiplier;
    } else if (data.age <= SCORING_PARAMS.comfortMultiplierByAge.middleAge.maxAge) {
      comfortMultiplier = SCORING_PARAMS.comfortMultiplierByAge.middleAge.multiplier;
    }

    const basePoints = comfortCount * SCORING_PARAMS.comfortIssueBasePoints;
    const scaledPoints = Math.round(basePoints * comfortMultiplier);
    assessmentScore += scaledPoints;

    // Show both raw and scaled in breakdown for transparency
    const value = comfortMultiplier < 1.0
      ? `${comfortCount} issue${comfortCount > 1 ? 's' : ''} (scaled for age)`
      : `${comfortCount} issue${comfortCount > 1 ? 's' : ''}`;

    scoreBreakdown.push({
      factor: 'Comfort Issues',
      value,
      points: scaledPoints
    });
  }

  // Duct condition weighting
  if (data.ductCondition) {
    const ductPts = SCORING_PARAMS.ductConditionPoints[data.ductCondition] || 0;
    assessmentScore += ductPts;
    const label = data.ductCondition.charAt(0).toUpperCase() + data.ductCondition.slice(1);
    scoreBreakdown.push({
      factor: 'Ductwork Condition',
      value: label,
      points: ductPts
    });
  }

  // Apply young system penalty to score
  assessmentScore = Math.max(0, assessmentScore - youngSystemPenalty);

  return {
    assessmentScore,
    scoreBreakdown,
    comfortCount,
    issueCategories: issueCats
  };
}

/**
 * Determine recommendation based on all factors
 * @param {Object} data - System data
 * @param {number} assessmentScore - Calculated assessment score
 * @param {number} comfortCount - Number of comfort issues
 * @returns {string} Recommendation ('REPAIR', 'CONSIDER', 'REPLACE', 'MAINTAIN')
 */
export function determineRecommendation(data, assessmentScore, comfortCount) {
  const threshold = AGE_THRESHOLDS[data.age] || 200;

  // Adjust repair threshold for young systems
  let adjustedThreshold = threshold;
  if (data.age < RECOMMENDATION_THRESHOLDS.youngSystem.veryYoung.maxAge) {
    adjustedThreshold = threshold * RECOMMENDATION_THRESHOLDS.youngSystem.veryYoung.thresholdMultiplier;
  } else if (data.age < RECOMMENDATION_THRESHOLDS.youngSystem.young.maxAge) {
    adjustedThreshold = threshold * RECOMMENDATION_THRESHOLDS.youngSystem.young.thresholdMultiplier;
  }

  let recommendation;

  // Special handling for maintenance visit / proactive assessment (no repair needed)
  if (!data.repairCost || data.repairCost === 0) {
    // Strong bias toward maintenance for young systems
    if (data.age < 5) {
      recommendation = 'MAINTAIN'; // Always maintain systems under 5 years
    } else if (data.age < 8 && comfortCount < 4) {
      recommendation = 'MAINTAIN'; // Maintain younger systems unless major issues
    } else if (data.age >= 12 || assessmentScore >= 60) {
      recommendation = 'REPLACE';
    } else if (data.age >= 10 || assessmentScore >= 40) {
      recommendation = 'CONSIDER';
    } else {
      recommendation = 'MAINTAIN';
    }
  } else {
    // Standard repair vs replace logic with young system protection
    if (data.age < RECOMMENDATION_THRESHOLDS.youngSystem.veryYoung.maxAge) {
      // Very young systems: strong bias toward repair
      if (data.repairCost < adjustedThreshold * RECOMMENDATION_THRESHOLDS.youngSystem.veryYoung.repairThreshold) {
        recommendation = 'REPAIR';
      } else {
        recommendation = 'CONSIDER'; // Never jump straight to REPLACE for young systems
      }
    } else if (data.age < RECOMMENDATION_THRESHOLDS.youngSystem.young.maxAge) {
      // Young systems: moderate bias toward repair
      if (data.repairCost < adjustedThreshold * RECOMMENDATION_THRESHOLDS.youngSystem.young.repairThreshold) {
        recommendation = 'REPAIR';
      } else if (data.repairCost <= adjustedThreshold * RECOMMENDATION_THRESHOLDS.youngSystem.young.considerThreshold) {
        recommendation = 'CONSIDER';
      } else {
        recommendation = comfortCount >= 4 ? 'REPLACE' : 'CONSIDER';
      }
    } else {
      // Standard logic for older systems
      if (data.repairCost < adjustedThreshold * RECOMMENDATION_THRESHOLDS.standard.repairThreshold) {
        recommendation = 'REPAIR';
      } else if (data.repairCost <= adjustedThreshold * RECOMMENDATION_THRESHOLDS.standard.considerThreshold) {
        recommendation = 'CONSIDER';
      } else {
        recommendation = 'REPLACE';
      }

      // Adjust for score (ducts amplify)
      if (assessmentScore >= RECOMMENDATION_THRESHOLDS.scoreThresholds.considerScore && recommendation === 'REPAIR') {
        recommendation = 'CONSIDER';
      } else if (assessmentScore >= RECOMMENDATION_THRESHOLDS.scoreThresholds.replaceScore && recommendation === 'CONSIDER') {
        recommendation = 'REPLACE';
      }
    }

    // Additional nudge: very poor ducts tilt away from REPAIR
    if (data.ductCondition === 'poor' && recommendation === 'REPAIR') {
      recommendation = 'CONSIDER';
    }
  }

  return {
    recommendation,
    threshold: adjustedThreshold,
    repairVsThreshold: data.repairCost ? (data.repairCost / adjustedThreshold) * 100 : 0
  };
}

/**
 * Main analysis engine - orchestrates all calculations
 * @param {Object} data - Complete system data
 * @param {Object} climateData - Optional climate data (cooling/heating hours)
 * @param {Object} customRates - Optional custom utility rates {electricRate, gasRate}
 * @returns {Object} Complete analysis results
 */
export function analyzeSystem(data, climateData = null, customRates = null) {
  // Use provided climate data or fall back to default (Zone 4A)
  const climate = climateData || CLIMATE_ZONE_4A;

  // Calculate score
  const scoreResult = calculateAssessmentScore(data);

  // Determine recommendation
  const recommendationResult = determineRecommendation(
    data,
    scoreResult.assessmentScore,
    scoreResult.comfortCount
  );

  // Efficiency calculations (pass climate data and custom rates)
  const currentSeer = calculateCurrentSeer(data);
  const efficiencyAnalysis = calculateEfficiencySavings(currentSeer, data.annualEnergyCost, data, climate, customRates);

  // Maintenance projections (pass climate data)
  const maintProj = computeMaintenanceProjection(data, climate);
  const fiveYearMaintCurrent = maintProj.fiveYearTotal;
  const fiveYearMaintNew = maintProj.newSystemFiveYear;
  const tenYearMaintSavings = (fiveYearMaintCurrent - fiveYearMaintNew) * 2;

  // Energy savings 10y (use "Standard" as conservative)
  const tenYearEnergySavings = efficiencyAnalysis.newOptions[0].tenYearSavings;

  // Total recent investment
  const totalRecentInvestment = data.repairCost + (data.lastRepairCost || 0);

  return {
    ...recommendationResult,
    ...scoreResult,
    currentSeer,
    efficiencyAnalysis,
    maintProj,
    totalRecentInvestment,
    savings: {
      tenYearEnergy: tenYearEnergySavings,
      tenYearMaintenance: tenYearMaintSavings,
      totalTenYearBenefit: tenYearEnergySavings + tenYearMaintSavings
    },
    baseData: data,
    climateData: climate // Include climate data in results for transparency
  };
}

/* =========================
   System Option Energy Cost Calculations
   ========================= */

/**
 * Calculate annual energy cost for a NEW system option
 * This uses the system_option's SEER2/AFUE ratings, NOT the current system's ratings
 * @param {Object} params - Calculation parameters
 * @returns {number} Estimated annual energy cost for the new system
 */
export function calculateSystemOptionEnergyCost(params) {
  const {
    seer2,            // From system_options table
    afue,             // From system_options table (for gas systems)
    systemType,
    squareFootage,
    climateData = CLIMATE_ZONE_4A,
    envelope = null,
    electricRate = ENERGY_COST_PARAMS.electricRate,
    gasRate = GAS_PRICE_NC
  } = params;

  // Calculate cooling load (BTU per hour)
  let coolingLoadBtu = squareFootage * 20;

  // Apply envelope adjustments if provided
  if (envelope) {
    const envelopeScore = calculateEnvelopeScore(envelope);
    coolingLoadBtu *= envelopeScore;
  }

  // NEW SYSTEM: Assume excellent duct efficiency (95%)
  // New installations typically have sealed ducts and proper sizing
  const ductEfficiency = 0.95;

  // Calculate annual cooling cost (electric)
  let annualCoolingCost = 0;
  if (systemType !== 'furnaceOnly' && systemType !== 'boiler') {
    const coolingHours = climateData.coolingHours;
    const annualCoolingKwh = (coolingLoadBtu * coolingHours) / (seer2 * 1000) / ductEfficiency;
    annualCoolingCost = annualCoolingKwh * electricRate;
  }

  // Calculate annual heating cost based on system type
  let annualHeatingCost = 0;
  const heatingLoadBtu = squareFootage * 30;
  const heatingHours = climateData.heatingHours;

  if (systemType === 'furnaceAC' || systemType === 'furnaceACPackage' || systemType === 'furnaceOnly' || systemType === 'boiler') {
    // Gas heating systems - use provided AFUE from system_option
    const systemAFUE = afue || 80; // Default to 80% if not provided
    // AFUE is percentage, convert to decimal
    const annualTherms = (heatingLoadBtu * heatingHours) / ((systemAFUE / 100) * 100000);
    annualHeatingCost = annualTherms * gasRate;
  } else if (systemType === 'heatPump' || systemType === 'heatPumpPackage') {
    // Heat pump - electric heating
    // Estimate HSPF2 from SEER2 (modern systems: HSPF2 ≈ SEER2 * 0.85)
    const estimatedHSPF2 = seer2 * 0.85;
    const annualHeatingKwh = (heatingLoadBtu * heatingHours) / (estimatedHSPF2 * 1000) / ductEfficiency;
    annualHeatingCost = annualHeatingKwh * electricRate;
  } else if (systemType === 'ductlessMini') {
    // Ductless mini-split heating (very efficient, no duct losses)
    const estimatedHSPF2 = seer2 * 0.90;
    const annualHeatingKwh = (heatingLoadBtu * heatingHours) / (estimatedHSPF2 * 1000);
    annualHeatingCost = annualHeatingKwh * electricRate;
  }

  // Total annual cost (no comfort issue multipliers for NEW system)
  const totalAnnual = annualCoolingCost + annualHeatingCost;

  return Math.round(totalAnnual);
}

/* =========================
   Comfort Issue Cost Quantification
   ========================= */

/**
 * Calculate the financial impact of comfort issues
 * Quantifies the "emotional pain" in dollars to make it tangible
 * @param {Object} issues - Comfort issues object (hotCold, humidity, runsConstantly, etc.)
 * @param {number} baseEnergyCost - Current annual energy cost
 * @param {number} squareFootage - Home size
 * @returns {Object} Breakdown of comfort issue costs
 */
export function calculateComfortIssueCosts(issues, baseEnergyCost, squareFootage) {
  const costs = {
    hotCold: 0,
    humidity: 0,
    runsConstantly: 0,
    highBills: 0,
    loud: 0,
    tempTrouble: 0,
    total: 0
  };

  // Hot/cold spots: Extra energy + comfort degradation
  // Implies poor ductwork or sizing issues
  if (issues.hotCold) {
    costs.hotCold = Math.round(baseEnergyCost * 0.10); // 10% waste
  }

  // Humidity issues: Extra runtime trying to compensate
  if (issues.humidity) {
    costs.humidity = Math.round(baseEnergyCost * 0.05); // 5% waste
  }

  // Runs constantly: System working harder than it should
  if (issues.runsConstantly) {
    costs.runsConstantly = Math.round(baseEnergyCost * 0.15); // 15% waste
  }

  // High bills: Already captured in base cost, but indicates overall inefficiency
  if (issues.highBills) {
    costs.highBills = Math.round(baseEnergyCost * 0.25); // 25% above normal
  }

  // Loud noise: Not an energy cost, but maintenance issue indicator
  // Estimate repair likelihood cost
  if (issues.loud) {
    costs.loud = 250; // Average diagnostic + minor repair
  }

  // Temperature trouble: System can't keep up = undersized or failing
  if (issues.tempTrouble) {
    costs.tempTrouble = Math.round(baseEnergyCost * 0.12); // 12% waste
  }

  // Calculate total
  costs.total = Object.values(costs).reduce((sum, val) => sum + val, 0) - costs.total; // Exclude total from sum

  return costs;
}

/* =========================
   Current System Cost Projections
   ========================= */

/**
 * Project costs for keeping the current system over multiple years
 * Includes energy costs, maintenance, and failure probability
 * @param {Object} params - Projection parameters
 * @returns {Object} Year-by-year projection with totals
 */
export function projectCurrentSystemCosts(params) {
  const {
    currentAge,
    annualEnergyCost,
    squareFootage,
    systemType,
    repairCost,
    yearsToProject = 5
  } = params;

  const projections = [];
  const utilityEscalation = 0.03; // 3% annual increase
  let cumulativeEnergy = 0;
  let cumulativeMaintenance = 0;
  let cumulativeRepairs = 0;

  for (let year = 1; year <= yearsToProject; year++) {
    const systemAgeInYear = currentAge + year;

    // Energy cost with escalation
    const energyCost = Math.round(annualEnergyCost * Math.pow(1 + utilityEscalation, year));
    cumulativeEnergy += energyCost;

    // Maintenance cost (increases with age)
    let maintenanceCost = 150; // Base annual maintenance
    if (systemAgeInYear > 10) maintenanceCost += 50;
    if (systemAgeInYear > 15) maintenanceCost += 100;
    if (systemAgeInYear > 20) maintenanceCost += 150;
    cumulativeMaintenance += maintenanceCost;

    // Repair probability and cost
    let failureProbability = 0;
    if (systemAgeInYear >= 8 && systemAgeInYear < 12) failureProbability = 0.10;
    else if (systemAgeInYear >= 12 && systemAgeInYear < 15) failureProbability = 0.20;
    else if (systemAgeInYear >= 15 && systemAgeInYear < 18) failureProbability = 0.35;
    else if (systemAgeInYear >= 18 && systemAgeInYear < 22) failureProbability = 0.50;
    else if (systemAgeInYear >= 22) failureProbability = 0.70;

    // Average major repair cost (compressor, blower motor, heat exchanger, etc.)
    const avgRepairCost = systemType.includes('heatPump') ? 1800 : 1400;
    const expectedRepairCost = Math.round(failureProbability * avgRepairCost);
    cumulativeRepairs += expectedRepairCost;

    projections.push({
      year,
      systemAge: systemAgeInYear,
      energyCost,
      maintenanceCost,
      failureProbability: Math.round(failureProbability * 100), // As percentage
      expectedRepairCost,
      totalCost: energyCost + maintenanceCost + expectedRepairCost,
      cumulativeTotal: cumulativeEnergy + cumulativeMaintenance + cumulativeRepairs
    });
  }

  return {
    projections,
    totals: {
      energy: cumulativeEnergy,
      maintenance: cumulativeMaintenance,
      repairs: cumulativeRepairs,
      grand: cumulativeEnergy + cumulativeMaintenance + cumulativeRepairs
    }
  };
}

/* =========================
   Future Equipment Cost Projection
   ========================= */

/**
 * Calculate future cost of equipment with inflation
 * HVAC equipment inflation averages 5.5% annually (compounding)
 * @param {number} currentPrice - Today's system price
 * @param {number} yearsFromNow - Years to project into future
 * @param {number} inflationRate - Annual inflation rate (default 5.5%)
 * @returns {Object} Future cost breakdown
 */
export function calculateFutureEquipmentCost(currentPrice, yearsFromNow, inflationRate = 0.055) {
  const futurePrice = currentPrice * Math.pow(1 + inflationRate, yearsFromNow);
  const inflationAdded = futurePrice - currentPrice;

  return {
    currentPrice: Math.round(currentPrice),
    yearsFromNow,
    futurePrice: Math.round(futurePrice),
    inflationAdded: Math.round(inflationAdded),
    savingsByActingNow: Math.round(inflationAdded)
  };
}

/* =========================
   Utility Functions
   ========================= */

/**
 * Get system type label for display
 * @param {string} systemType - System type key
 * @returns {string} Display label
 */
export function getSystemTypeLabel(systemType) {
  const labels = {
    centralAC: 'Central AC',
    heatPump: 'Heat Pump',
    furnaceAC: 'Furnace + AC',
    ductlessMini: 'Ductless Mini-Split',
    furnaceOnly: 'Gas Furnace'
  };
  return labels[systemType] || systemType;
}

/**
 * Validate input data
 * @param {Object} data - System data to validate
 * @returns {Object} Validation result with errors array
 */
export function validateSystemData(data) {
  const errors = [];

  if (data.age < VALIDATION_LIMITS.systemAge.min || data.age > VALIDATION_LIMITS.systemAge.max) {
    errors.push(`System age must be between ${VALIDATION_LIMITS.systemAge.min} and ${VALIDATION_LIMITS.systemAge.max} years`);
  }

  if (data.squareFootage < VALIDATION_LIMITS.squareFootage.min || data.squareFootage > VALIDATION_LIMITS.squareFootage.max) {
    errors.push(`Square footage must be between ${VALIDATION_LIMITS.squareFootage.min} and ${VALIDATION_LIMITS.squareFootage.max}`);
  }

  if (data.repairCost < VALIDATION_LIMITS.repairCost.min || data.repairCost > VALIDATION_LIMITS.repairCost.max) {
    errors.push(`Repair cost must be between $${VALIDATION_LIMITS.repairCost.min} and $${VALIDATION_LIMITS.repairCost.max}`);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}
